import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Input';
import { PaginaCrud, type CampoFormulario } from '../../components/layout/PaginaCrud';
import type { FilaInventario } from '../../../data/repositories/RepositorioInventario';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { repositorioTipos } from '../../../data/repositories/RepositorioTipos';
import { Dominio, DOMINIOS } from '../../../domain/value-objects/Dominio';
import { aNumero, aTexto } from '../../../data/mappers/conversiones';
import estilos from '../../components/layout/PaginaCrud.module.css';

/*
 * Tiempos de partida de un sensor nuevo, los mismos que traía la aplicación
 * anterior: una lectura por minuto y una hora de historial en la gráfica.
 */
const INTERVALO_POR_DEFECTO_S = 60;
const VENTANA_POR_DEFECTO_S = 3600;

interface Sensor {
  id: number;
  nombre: string;
  tipo: string;
  unidad: string | null;
  codigoEnvio: string | null;
  estado: string;
  categoria: string | null;
  nodoNombre: string;
  areaNombre: string;
  clienteNombre: string;
}

export function Sensores() {
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [areaId, setAreaId] = useState<number | null>(null);
  const [nodoFiltroId, setNodoFiltroId] = useState<number | null>(null);

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const areasFiltro = useQuery({
    queryKey: ['catalogo', 'areas', clienteId],
    queryFn: () => repositorioCatalogos.areasDeCliente(clienteId),
    enabled: clienteId !== null,
    staleTime: 5 * 60_000,
  });

  const nodosFiltro = useQuery({
    queryKey: ['catalogo', 'nodos', areaId],
    queryFn: () => repositorioCatalogos.nodosDeArea(areaId),
    enabled: areaId !== null,
    staleTime: 5 * 60_000,
  });

  /*
   * Para la cascada del formulario hacen falta las listas enteras, no las que
   * ya vienen acotadas por los filtros de la barra: son cosas distintas y al
   * crear un sensor hay que poder elegir cualquier cliente.
   */
  const areasFormulario = useQuery({
    queryKey: ['catalogo', 'areas', 'con-cliente'],
    queryFn: () => repositorioCatalogos.areasConCliente(),
    staleTime: 5 * 60_000,
  });

  const nodos = useQuery({
    queryKey: ['catalogo', 'nodos', 'con-area'],
    queryFn: () => repositorioCatalogos.nodosConArea(),
    staleTime: 5 * 60_000,
  });

  const tipos = useQuery({
    queryKey: ['tipos', 'sensores'],
    queryFn: () => repositorioTipos.tiposDeSensor(),
    staleTime: 5 * 60_000,
  });

  // La vista de sensores no trae el tipo ni la categoría, y el formulario los
  // necesita para abrir con lo que el sensor ya tiene.
  const detalles = useQuery({
    queryKey: ['sensores', 'detalles'],
    queryFn: () => repositorioTipos.detallesDeSensores(),
  });

  const mapear = useCallback(
    (fila: FilaInventario): Sensor => {
      const id = aNumero(fila.sensor_id) ?? 0;
      return {
        id,
        nombre: aTexto(fila.nombre_sensor) ?? 'Sin nombre',
        tipo: aTexto(fila.tipo_sensor) ?? '—',
        unidad: aTexto(fila.unidad_simbolo),
        codigoEnvio: aTexto(fila.codigo_envio),
        estado: aTexto(fila.estado) ?? 'activo',
        categoria: detalles.data?.get(id)?.categoria ?? null,
        nodoNombre: aTexto(fila.nodo_nombre) ?? '—',
        areaNombre: aTexto(fila.area_nombre) ?? '—',
        clienteNombre: aTexto(fila.cliente_nombre) ?? '—',
      };
    },
    [detalles.data],
  );

  const campos: CampoFormulario[] = useMemo(
    () => [
      /*
       * Cliente y área no se guardan en `sensores` —el sensor cuelga del nodo,
       * y el nodo ya sabe dónde está—, pero sin ellos el desplegable de nodos
       * es la lista de todos los nodos de todas las instalaciones. Están para
       * acotar, igual que en la aplicación anterior.
       */
      {
        clave: 'cliente_id',
        etiqueta: 'Cliente',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un cliente',
        limpiaAlCambiar: ['area_id', 'nodo_id'],
        opciones: (clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre })),
      },
      {
        clave: 'area_id',
        etiqueta: 'Área',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un área',
        limpiaAlCambiar: ['nodo_id'],
        opciones: (valores) =>
          (areasFormulario.data ?? [])
            .filter((a) => String(a.clienteId ?? '') === String(valores.cliente_id ?? ''))
            .map((a) => ({ valor: a.id, etiqueta: a.nombre })),
      },
      {
        clave: 'nombre',
        etiqueta: 'Nombre',
        tipo: 'texto',
        requerido: true,
        placeholder: 'Temperatura cava, Puerta almacén…',
      },
      {
        clave: 'tipo_id',
        etiqueta: 'Tipo de sensor',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un tipo',
        ayuda: 'Determina si mide un valor o un estado, y en qué unidad',
        opciones: (tipos.data ?? []).map((t) => ({
          valor: t.id,
          etiqueta: t.unidad ? `${t.nombre} (${t.unidad})` : t.nombre,
        })),
      },
      {
        clave: 'nodo_id',
        etiqueta: 'Nodo',
        tipo: 'select',
        requerido: true,
        placeholder: 'Elige un nodo',
        ayuda: 'Equipo al que está conectado. Se acota al área elegida',
        opciones: (valores) =>
          (nodos.data ?? [])
            .filter((n) => String(n.areaId ?? '') === String(valores.area_id ?? ''))
            .map((n) => ({ valor: n.id, etiqueta: n.nombre })),
      },
      {
        clave: 'codigo_envio',
        etiqueta: 'Código de envío',
        tipo: 'texto',
        ayuda: 'Identificador con el que el nodo manda las lecturas de este sensor',
      },
      {
        clave: 'categoria',
        etiqueta: 'Categoría',
        tipo: 'select',
        placeholder: 'Sin categoría',
        ayuda: 'Agrupa el sensor en el monitoreo',
        opciones: DOMINIOS.map((d) => ({ valor: d, etiqueta: Dominio.from(d).etiqueta })),
      },
      {
        clave: 'estado',
        etiqueta: 'Estado',
        tipo: 'select',
        requerido: true,
        inicial: 'activo',
        opciones: [
          { valor: 'activo', etiqueta: 'Activo' },
          { valor: 'inactivo', etiqueta: 'Inactivo' },
        ],
      },
      {
        clave: 'intervalo_medicion',
        etiqueta: 'Intervalo de medición (s)',
        tipo: 'numero',
        requerido: true,
        inicial: INTERVALO_POR_DEFECTO_S,
        ayuda: 'Cada cuánto se espera una lectura de este sensor',
      },
      {
        clave: 'ventana_visualizacion',
        etiqueta: 'Ventana de la gráfica (s)',
        tipo: 'numero',
        requerido: true,
        inicial: VENTANA_POR_DEFECTO_S,
        ayuda: 'Cuánto historial se dibuja al abrir el sensor',
      },
      {
        clave: 'soporta_modo_arranque',
        etiqueta: 'Admite condiciones de arranque',
        tipo: 'checkbox',
        ayuda:
          'Permite que sus umbrales ignoren el sensor durante unos segundos al encender el equipo, ' +
          'mientras los valores se estabilizan',
      },
    ],
    [clientes.data, areasFormulario.data, tipos.data, nodos.data],
  );

  return (
    <PaginaCrud<Sensor>
      entidad="sensores"
      titulo="Sensores"
      descripcion="Puntos de medición conectados a los nodos."
      textoVacio="Da de alta un sensor para empezar a registrar mediciones."
      mapear={mapear}
      claveFila={(s) => s.id}
      textoBusqueda={(s) =>
        `${s.nombre} ${s.tipo} ${s.categoria ? Dominio.from(s.categoria).etiqueta : ''} ${s.codigoEnvio ?? ''} ${s.nodoNombre} ${s.areaNombre} ${s.clienteNombre}`
      }
      idDeFila={(fila) => aNumero(fila.sensor_id) ?? 0}
      campos={campos}
      filtrosConsulta={{ cliente_id: clienteId, area_id: areaId, nodo_id: nodoFiltroId }}
      filtrosExtra={
        <>
          <Select
            etiqueta="Cliente"
            placeholder="Todos los clientes"
            value={clienteId ?? ''}
            onChange={(e) => {
              setClienteId(e.target.value ? Number(e.target.value) : null);
              setAreaId(null);
              setNodoFiltroId(null);
            }}
            opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
          />
          <Select
            etiqueta="Área"
            placeholder={clienteId ? 'Todas las áreas' : 'Elige un cliente'}
            disabled={!clienteId}
            value={areaId ?? ''}
            onChange={(e) => {
              setAreaId(e.target.value ? Number(e.target.value) : null);
              setNodoFiltroId(null);
            }}
            opciones={(areasFiltro.data ?? []).map((a) => ({ valor: a.id, etiqueta: a.nombre }))}
          />
          <Select
            etiqueta="Nodo"
            placeholder={areaId ? 'Todos los nodos' : 'Elige un área'}
            disabled={!areaId}
            value={nodoFiltroId ?? ''}
            onChange={(e) => setNodoFiltroId(e.target.value ? Number(e.target.value) : null)}
            opciones={(nodosFiltro.data ?? []).map((n) => ({ valor: n.id, etiqueta: n.nombre }))}
          />
        </>
      }
      aFormulario={(fila) => {
        const detalle = detalles.data?.get(aNumero(fila.sensor_id) ?? 0);

        return {
          // Cliente y área vienen de la vista sólo para dejar la cascada
          // abierta por donde está el sensor; no se guardan.
          cliente_id: aNumero(fila.cliente_id) ?? '',
          area_id: aNumero(fila.area_id) ?? '',
          nombre: aTexto(fila.nombre_sensor) ?? '',
          tipo_id: detalle?.tipoId ?? '',
          nodo_id: aNumero(fila.nodo_id) ?? '',
          codigo_envio: aTexto(fila.codigo_envio) ?? '',
          categoria: detalle?.categoria ?? '',
          estado: aTexto(fila.estado) ?? 'activo',
          intervalo_medicion: detalle?.intervaloSegundos ?? INTERVALO_POR_DEFECTO_S,
          ventana_visualizacion: detalle?.ventanaSegundos ?? VENTANA_POR_DEFECTO_S,
          soporta_modo_arranque: detalle?.modoArranque ?? false,
        };
      }}
      /*
       * Cliente y área se quedan fuera a propósito: la tabla `sensores` no
       * tiene esas columnas, el sensor pertenece al nodo y el nodo al área.
       */
      aRegistro={(v) => ({
        nombre: String(v.nombre ?? '').trim(),
        tipo_id: Number(v.tipo_id),
        nodo_id: Number(v.nodo_id),
        codigo_envio: String(v.codigo_envio ?? '').trim() || null,
        categoria: v.categoria ? String(v.categoria) : null,
        estado: v.estado,
        intervalo_medicion: Number(v.intervalo_medicion) || INTERVALO_POR_DEFECTO_S,
        ventana_visualizacion: Number(v.ventana_visualizacion) || VENTANA_POR_DEFECTO_S,
        soporta_modo_arranque: v.soporta_modo_arranque === true,
      })}
      textoBorrado={(s) =>
        `¿Eliminar el sensor «${s.nombre}»?\n\n` +
        'Se borrarán también sus umbrales, sus alarmas y todo su historial de mediciones.'
      }
      columnas={[
        {
          clave: 'nombre',
          encabezado: 'Sensor',
          movil: 'titulo',
          render: (s) => <span className={estilos.nombre}>{s.nombre}</span>,
        },
        {
          clave: 'ubicacion',
          encabezado: 'Ubicación',
          movil: 'subtitulo',
          render: (s) => (
            <span className={estilos.secundario}>
              {s.clienteNombre} · {s.areaNombre} · {s.nodoNombre}
            </span>
          ),
        },
        /*
         * La categoría es lo que agrupa los sensores en el monitoreo, así que
         * ver aquí cuáles se quedaron sin ella evita que aparezcan sueltos
         * allí. Se pinta con el color del propio dominio, el mismo que usa la
         * pantalla de monitoreo, para que se reconozcan de un vistazo.
         */
        {
          clave: 'categoria',
          encabezado: 'Categoría',
          movil: 'detalle',
          render: (s) => {
            if (!s.categoria) return <span className={estilos.secundario}>Sin categoría</span>;
            const dominio = Dominio.from(s.categoria);
            return <Badge color={dominio.color}>{dominio.etiqueta}</Badge>;
          },
        },
        {
          clave: 'tipo',
          encabezado: 'Tipo',
          render: (s) => (s.unidad ? `${s.tipo} (${s.unidad})` : s.tipo),
        },
        {
          clave: 'codigo',
          encabezado: 'Código de envío',
          render: (s) => <span className={estilos.secundario}>{s.codigoEnvio ?? '—'}</span>,
        },
        {
          clave: 'estado',
          encabezado: 'Estado',
          movil: 'detalle',
          render: (s) => (
            <Badge color={s.estado === 'activo' ? 'var(--status-success)' : 'var(--text-3)'}>
              {s.estado}
            </Badge>
          ),
        },
      ]}
    />
  );
}
